import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { Mail, Instagram, Youtube, Music2 } from "lucide-react";

const fmt = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return n.toString();
};

export default function PublicMediaKit() {
  const { handle } = useParams();
  const clean = (handle || "").replace(/^@/, "");
  const [kit, setKit] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/mediakit/public/${clean}`).then((r) => setKit(r.data)).catch((e) =>
      setErr(e.response?.data?.detail || "Not found"),
    );
  }, [clean]);

  if (err)
    return (
      <div className="max-w-xl mx-auto px-6 py-32 text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-neutral-500 mb-4">// 404</p>
        <h1 className="font-display font-black text-4xl mb-4">Media kit not found.</h1>
        <Link to="/" className="text-[#CCFF00] hover:underline">Back to DAP</Link>
      </div>
    );

  if (!kit) return <div className="max-w-3xl mx-auto p-8 text-neutral-500">Loading…</div>;

  const totalFollowers =
    (kit.followers_instagram || 0) + (kit.followers_tiktok || 0) + (kit.followers_youtube || 0);

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <SEO
        title={`${kit.display_name} · Media Kit`}
        description={kit.tagline || `${kit.display_name} is a creator open to brand partnerships. See stats, past brands, and rates on DAP.`}
        path={`/${kit.handle}`}
        type="profile"
      />
      {/* Header */}
      <div className="mb-16">
        <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-3">// media kit</p>
        <h1
          className="font-display font-black text-6xl md:text-8xl tracking-tighter leading-none"
          data-testid="public-mk-name"
        >
          {kit.display_name}
        </h1>
        <p className="mt-4 font-mono text-neutral-400" data-testid="public-mk-handle">@{kit.handle}</p>
        {kit.tagline && <p className="mt-6 text-xl text-neutral-300 max-w-2xl">{kit.tagline}</p>}
        {kit.niches?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {kit.niches.map((n) => (
              <span
                key={n}
                className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-mono uppercase tracking-widest"
              >
                {n}
              </span>
            ))}
          </div>
        )}
        {kit.email_public && (
          <div className="mt-8">
            <a href={`mailto:${kit.email_public}`} data-testid="public-mk-contact-btn">
              <Button className="rounded-full h-12 px-6 bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold hover:scale-105 transition-transform">
                <Mail className="w-4 h-4" /> Work with me
              </Button>
            </a>
          </div>
        )}
      </div>

      {/* Big Numbers - Bento */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12" data-testid="public-mk-stats">
        <BigNum label="Total reach" value={fmt(totalFollowers)} accent />
        <BigNum label="Avg views" value={fmt(kit.avg_views)} />
        <BigNum label="Engagement" value={kit.engagement_rate ? `${kit.engagement_rate}%` : "—"} />
        <BigNum
          label="Starting rate"
          value={kit.starting_rate ? `$${kit.starting_rate.toLocaleString()}` : "—"}
        />
      </div>

      {/* Platforms */}
      <div className="grid md:grid-cols-3 gap-3 mb-12">
        <PlatformCard
          icon={Instagram}
          name="Instagram"
          handle={kit.instagram_handle}
          followers={kit.followers_instagram}
        />
        <PlatformCard icon={Music2} name="TikTok" handle={kit.tiktok_handle} followers={kit.followers_tiktok} />
        <PlatformCard
          icon={Youtube}
          name="YouTube"
          handle={kit.youtube_handle}
          followers={kit.followers_youtube}
        />
      </div>

      {/* Audience */}
      {(kit.audience_female_pct || kit.audience_age_range || kit.top_countries?.length) && (
        <div className="p-8 rounded-2xl bg-[#141414] border border-white/5 mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-6">// audience</p>
          <div className="grid md:grid-cols-3 gap-6">
            {kit.audience_female_pct && (
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-neutral-500">Female</p>
                <p className="font-display font-black text-4xl mt-1">{kit.audience_female_pct}%</p>
              </div>
            )}
            {kit.audience_age_range && (
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-neutral-500">Age range</p>
                <p className="font-display font-black text-4xl mt-1">{kit.audience_age_range}</p>
              </div>
            )}
            {kit.top_countries?.length > 0 && (
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-neutral-500">Top countries</p>
                <p className="mt-2 text-neutral-200">{kit.top_countries.join(" · ")}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Past brands */}
      {kit.past_brands?.length > 0 && (
        <div className="p-8 rounded-2xl bg-[#141414] border border-white/5 mb-12">
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-6">// past partners</p>
          <div className="flex flex-wrap gap-3">
            {kit.past_brands.map((b) => (
              <span
                key={b}
                className="px-4 py-2 rounded-lg bg-[#0e0e0e] border border-white/10 font-display font-bold"
              >
                {b}
              </span>
            ))}
          </div>
        </div>
      )}

      {kit.bio && <p className="mt-8 text-neutral-300 leading-relaxed max-w-3xl">{kit.bio}</p>}

      <footer className="mt-24 pt-8 border-t border-white/5 text-xs font-mono text-neutral-500">
        Made with <Link to="/" className="text-[#CCFF00] hover:underline">DAP</Link> · Every deal starts with a DAP.
      </footer>
    </div>
  );
}

const BigNum = ({ label, value, accent }) => (
  <div
    className={`p-6 rounded-2xl ${
      accent ? "bg-[#CCFF00] text-black" : "bg-[#141414] border border-white/5"
    }`}
  >
    <p
      className={`text-[10px] font-mono uppercase tracking-widest ${
        accent ? "text-black/60" : "text-neutral-500"
      }`}
    >
      {label}
    </p>
    <p className="font-display font-black text-5xl mt-2 tracking-tighter leading-none">{value}</p>
  </div>
);

const PlatformCard = ({ icon: Icon, name, handle, followers }) => (
  <div className="p-6 rounded-2xl bg-[#141414] border border-white/5 flex items-center gap-4">
    <Icon className="w-6 h-6 text-neutral-400" />
    <div>
      <p className="font-mono text-xs text-neutral-500">{name}</p>
      <p className="font-display font-bold text-lg">
        {handle ? `@${handle}` : "—"}
      </p>
      <p className="font-mono text-xs text-[#CCFF00]">{fmt(followers)}</p>
    </div>
  </div>
);
